import { createSlice } from "@reduxjs/toolkit";
import {
  getATECasesStatisticsApi,
  getBMCRatingStatisticsAPI,
  getBuildingsStatisticsApi,
  getContractInformationStatisticsApi,
  getDashboardCardsApi,
  getMaintenanceAppointmentsStatisticsApi,
  getMaintenanceRequestsStatisticsApi,
} from "../../api/DashboardsAPI";
import { convertToISO, toCamel, toPascal } from "../../utils/helpers";
import { handleResponse } from "src/services/apiServices";
import {
  IStatisticsStatusData,
  IDashboard,
  IContractsStatusData,
  IBMCRatingStatistics,
} from "../interfaces/IDashboard";
import moment from "moment";

export const getMaintenanceAppointmentsStatistics = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsMaintenanceAppointmentsStatisticsDataLoaded(false));
    const { lookup } = getState();
    const language = lookup.language;
    const dashboardFilterPascal = await dispatch(formFilterData());

    getMaintenanceAppointmentsStatisticsApi(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        dispatch(setMaintenanceAppointmentsStatisticsData(data));
        dispatch(setMaintenanceAppointmentsTotalCount(data.totalCount));
        dispatch(setIsMaintenanceAppointmentsStatisticsDataLoaded(true));
      })
      .catch((err) => {
        handleResponse(err, language);
      });
  };
};
export const getMaintenanceRequestsStatistics = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsMaintenanceRequestsStatisticsDataLoaded(false));
    const { lookup } = getState();
    const language = lookup.language;

    const dashboardFilterPascal = await dispatch(formFilterData());

    getMaintenanceRequestsStatisticsApi(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        const mappedData: IStatisticsStatusData = {
          completed: data.completed,
          inProgress: data.inProgress,
          exceeded: data.exceeded,
          pending: data.pending,
          onHold: data.onHold,
        };
        dispatch(setMaintenanceRequestsStatisticsData(mappedData));
        dispatch(setMaintenanceRequestsTotalCount(data.totalCount));
        dispatch(setIsMaintenanceRequestsStatisticsDataLoaded(true));
      })
      .catch((err) => {
        handleResponse(err, language);
      });
  };
};
export const getContractInformationStatistics = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsContractInformationDataLoaded(false));
    const { lookup } = getState();
    const language = lookup.language;
    const dashboardFilterPascal = await dispatch(formFilterData());

    getContractInformationStatisticsApi(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        const mappedData: IContractsStatusData = {
          activeContracts: data.activeContracts,
          expiringIn90Days: data.expiringIn90Days,
          expiringIn30Days: data.expiringIn30Days,
        };
        dispatch(setContractInformationData(mappedData));
        dispatch(setContractInformationTotalCount(data.totalCount));
        dispatch(setIsContractInformationDataLoaded(true));
      })
      .catch((err) => {
        handleResponse(err, language);
      });
  };
};
export const getATECasesStatistics = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsAteCasesStatisticsDataLoaded(false));

    const { lookup } = getState();
    const language = lookup.language;

    const dashboardFilterPascal = await dispatch(formFilterData());

    getATECasesStatisticsApi(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        const mappedData: IStatisticsStatusData = {
          completed: data.completed,
          inProgress: data.inProgress,
          exceeded: data.exceeded,
          onHold: data.onHold,
        };
        dispatch(setATECasesStatisticsData(mappedData));
        dispatch(setATECasesTotalCount(data.totalCount));
        dispatch(setIsAteCasesStatisticsDataLoaded(true));
      })
      .catch((err) => {
        handleResponse(err, language);
      });
  };
};

export const getBMCRatingStatistics = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsBMCRatingStatisticsLoaded(false));
    const dashboardFilterPascal = await dispatch(formFilterData());

    getBMCRatingStatisticsAPI(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        const mappedData: IBMCRatingStatistics = {
          excellent: data.excellent,
          good: data.good,
          fair: data.fair,
          poor: data.poor,
          averagebmcsRatings: data.averagebmcsRatings,
        };
        dispatch(setBMCRatingStatistics(mappedData));
        dispatch(setBMCRatingStatisticsTotalCount(data.totalCount));
        dispatch(setIsBMCRatingStatisticsLoaded(true));
      })
      .catch((err) => {
        const { lookup } = getState();
        const language = lookup.language;
        handleResponse(err, language);
      });
  };
};

export const getDashboardCards = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsDashboardCardsDataLoaded(false));
    const { dashboard, lookup } = getState();
    const language = lookup.language;
    const { filterData } = dashboard;
    const dashboardFilterPascal = await dispatch(formFilterData());
    getDashboardCardsApi(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        if (data.companyReview == undefined)
          data["companyReview"] = data.averagebmcsRatings;
        if (!data.casesSummary) {
          data.casesSummary = {
            totalCount: 0,
            inProgress: 0,
            completed: 0,
            pending: 0,
            exceeded: 0,
            onHold: 0,
          };
        }
        dispatch(setDashboardCardsData(data));
        dispatch(setIsDashboardCardsDataLoaded(true));
      })
      .catch((err) => {
        handleResponse(err, language);
      });
  };
};

export const getBuildingsStatistics = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(setIsBuildingStatisticsDataLoaded(false));

    const { lookup } = getState();
    const language = lookup.language;
    const dashboardFilterPascal = await dispatch(formFilterData());

    getBuildingsStatisticsApi(dashboardFilterPascal)
      .then((res) => {
        const data = toCamel(res?.data || {});
        dispatch(setBuildingStats(data));
        dispatch(setIsBuildingStatisticsDataLoaded(true));
      })
      .catch((err) => {
        handleResponse(err, language);
      });
  };
};
export const formFilterData = () => {
  return async (dispatch: any, getState: any) => {
    const {
      dashboard,
      lookup: { serviceTerritories },
    } = getState();

    const { filterData } = dashboard;
    let { startDate, endDate, city, territoryIds, contractId } = filterData;
    startDate = convertToISO(startDate);
    endDate = convertToISO(endDate);

    const selectedPropertyIds = [];
    if (filterData.propertyId.length > 0) {
      for (const index in filterData.propertyId) {
        selectedPropertyIds.push(filterData.propertyId[index].propertyId);
      }
    }
    const allCityTerritories = serviceTerritories.map(
      (item: any) => item.territoryId
    );
    const flatContractIds = contractId.flat();
    const dashboardFilterPascal = {
      ...toPascal({
        ...filterData,
        startDate,
        endDate,
        propertyId: selectedPropertyIds,
        TerritoryIds: city ? territoryIds ?? allCityTerritories : null,
        ContractId: flatContractIds,
      }),
    };
    return dashboardFilterPascal;
  };
};
const initialState: IDashboard = {
  dashboardCardsData: {
    meetingSla: 0,
    avarageResiluationTime: 0,
    casesSummary: {
      totalCount: 0,
      inProgress: 0,
      completed: 0,
      pending: 0,
      exceeded: 0,
      onHold: 0,
    },
    companyReview: {
      ratingPercentage: 0,
      ratingText: "",
    },
  },
  buildingStats: {},
  ateCasesStatisticsData: {},
  contractInformationData: {
    activeContracts: 0,
    expiringIn30Days: 0,
    expiringIn90Days: 0,
  },
  maintenanceRequestsStatistics: {},
  maintenanceAppointmentsStatistics: {
    completed: 0,
    pending: 0,
    onHold: 0,
    inProgress: 0,
    exceeded: 0,
  },
  bmcRatingStatistics: {
    excellent: 0,
    good: 0,
    fair: 0,
    poor: 0,
  },
  filterData: {
    propertyId: [],
    startDate: moment().subtract(365, "days").toDate(),
    endDate: moment().endOf("year").toDate(),
    contractId: [],
    companyIds: [],
    city: "",
    territoryIds: [],
  },
  ateCasesTotalCount: 0,
  maintenanceRequestsTotalCount: 0,
  bmcRatingTotalCount: 0,
  maintenanceAppointmentsTotalCount: 0,
  contractInformationTotalCount: 0,
  isDashboardCardsDataLoaded: false,
  isBuildingStatisticsDataLoaded: false,
  isAteCasesStatisticsDataLoaded: false,
  isContractInformationDataLoaded: false,
  isMaintenanceRequestsStatisticsDataLoaded: false,
  isMaintenanceAppointmentsStatisticsLoaded: false,
  isBMCRatingDataLoaded: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setATECasesStatisticsData: (state, action) => {
      state.ateCasesStatisticsData = action.payload;
    },
    setBMCRatingStatistics: (state, action) => {
      state.bmcRatingStatistics = action.payload;
    },
    setContractInformationData: (state, action) => {
      state.contractInformationData = action.payload;
    },
    setMaintenanceAppointmentsStatisticsData: (state, action) => {
      state.maintenanceAppointmentsStatistics = action.payload;
    },
    setMaintenanceRequestsStatisticsData: (state, action) => {
      state.maintenanceRequestsStatistics = action.payload;
    },
    setDashboardCardsData: (state, action) => {
      state.dashboardCardsData = action.payload;
    },
    setBuildingStats: (state, action) => {
      state.buildingStats = action.payload;
    },
    setATECasesTotalCount: (state, action) => {
      state.ateCasesTotalCount = action.payload;
    },
    setMaintenanceAppointmentsTotalCount: (state, action) => {
      state.maintenanceAppointmentsTotalCount = action.payload;
    },
    setMaintenanceRequestsTotalCount: (state, action) => {
      state.maintenanceRequestsTotalCount = action.payload;
    },
    setBMCRatingStatisticsTotalCount: (state, action) => {
      state.bmcRatingTotalCount = action.payload;
    },
    setContractInformationTotalCount: (state, action) => {
      state.contractInformationTotalCount = action.payload;
    },

    setFilterData: (state, action) => {
      state.filterData = {
        ...state.filterData,
        [action.payload.field]: action.payload.value,
      };
    },
    resetFilterData: (state) => {
      state.filterData = initialState.filterData;
    },
    setIsDashboardCardsDataLoaded: (state, action) => {
      state.isDashboardCardsDataLoaded = action.payload;
    },
    setIsBuildingStatisticsDataLoaded: (state, action) => {
      state.isBuildingStatisticsDataLoaded = action.payload;
    },
    setIsAteCasesStatisticsDataLoaded: (state, action) => {
      state.isAteCasesStatisticsDataLoaded = action.payload;
    },
    setIsBMCRatingStatisticsLoaded: (state, action) => {
      state.isBMCRatingDataLoaded = action.payload;
    },
    setIsContractInformationDataLoaded: (state, action) => {
      state.isContractInformationDataLoaded = action.payload;
    },
    setIsMaintenanceRequestsStatisticsDataLoaded: (state, action) => {
      state.isMaintenanceRequestsStatisticsDataLoaded = action.payload;
    },
    setIsMaintenanceAppointmentsStatisticsDataLoaded: (state, action) => {
      state.isMaintenanceAppointmentsStatisticsLoaded = action.payload;
    },
  },
});

export const {
  setATECasesStatisticsData,
  setBMCRatingStatistics,
  setBMCRatingStatisticsTotalCount,
  setFilterData,
  setContractInformationData,
  setMaintenanceAppointmentsStatisticsData,
  setMaintenanceRequestsStatisticsData,
  setDashboardCardsData,
  setATECasesTotalCount,
  setMaintenanceAppointmentsTotalCount,
  setMaintenanceRequestsTotalCount,
  setContractInformationTotalCount,
  setBuildingStats,
  resetFilterData,
  setIsDashboardCardsDataLoaded,
  setIsBuildingStatisticsDataLoaded,
  setIsAteCasesStatisticsDataLoaded,
  setIsBMCRatingStatisticsLoaded,
  setIsContractInformationDataLoaded,
  setIsMaintenanceRequestsStatisticsDataLoaded,
  setIsMaintenanceAppointmentsStatisticsDataLoaded,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
